import { supabase } from '../supabaseClient';

// Cache object untuk menyimpan hasil query
const queryCache = new Map();

// Helper untuk cache query dengan TTL (Time To Live)
export const cachedQuery = async (key, queryFn, ttl = 5 * 60 * 1000) => {
  const cached = queryCache.get(key);
  
  if (cached && Date.now() - cached.timestamp < ttl) {
    return cached.data;
  }
  
  const data = await queryFn();
  queryCache.set(key, { data, timestamp: Date.now() });
  return data;
};

// Clear cache untuk key tertentu
export const clearCache = (key) => {
  if (key) {
    queryCache.delete(key);
  } else {
    queryCache.clear();
  }
};

// Optimized queries dengan select hanya field yang diperlukan
export const queries = {
  // Get user profile (minimal fields)
  getUserProfile: async (userId) => {
    return cachedQuery(`user_${userId}`, async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, full_name, email, role, photo')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      return data;
    });
  },

  // Get user full profile (semua fields)
  getUserFullProfile: async (userId) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) throw error;
    return data;
  },

  // Get projects dengan pagination
  getProjects: async (userId, { limit = 10, offset = 0 } = {}) => {
    return cachedQuery(`projects_${userId}_${limit}_${offset}`, async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('project_id, title, description, image_url, created_at, start_date, end_date, role')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (error) throw error;
      return data;
    });
  },

  // Get timesheets dengan filter bulan
  getTimesheets: async (userId, month, userRole = null) => {
    const cacheKey = `timesheets_${userId}_${month}_${userRole}`;
    return cachedQuery(cacheKey, async () => {
      // Hitung hari terakhir bulan dengan benar
      const [year, monthNum] = month.split('-');
      const lastDay = new Date(year, monthNum, 0).getDate();
      
      let query = supabase
        .from('timesheet')
        .select('id, date, date_end, time_from, time_to, jira_code, initiative, activity, status, user_id')
        .gte('date', `${month}-01`)
        .lte('date', `${month}-${lastDay.toString().padStart(2, '0')}`);
      
      // Admin dapat melihat semua data, user biasa hanya data mereka sendiri
      if (userRole !== 'admin') {
        query = query.eq('user_id', userId);
      }
      
      const { data, error } = await query.order('date', { ascending: false });
      
      if (error) throw error;
      return data;
    }, 2 * 60 * 1000); // 2 minutes cache
  },

  // Get all users (admin only)
  getAllUsers: async () => {
    return cachedQuery('all_users', async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, full_name, email, role, status, approved_by, approved_at, rejection_reason, created_at');
      
      if (error) throw error;
      return data;
    }, 10 * 60 * 1000); // 10 minutes cache
  },
};

// Batch loading untuk multiple queries
export const batchLoad = async (queries) => {
  return Promise.all(queries);
};

// Preload data untuk halaman tertentu
export const preloadData = {
  dashboard: async (userId) => {
    return batchLoad([
      queries.getUserProfile(userId),
      queries.getProjects(userId, { limit: 6 }),
    ]);
  },
  
  profile: async (userId) => {
    return queries.getUserFullProfile(userId);
  },
  
  timesheet: async (userId, month) => {
    return batchLoad([
      queries.getUserProfile(userId),
      queries.getTimesheets(userId, month),
    ]);
  },
};
